import { Option } from 'src/features/formRenderer/types';

type PumpModalData = {
  date: string;
  site: string;
  asset: string;
  runtime: string;
  cycles: string;
  flow: string;
  efficiency: string;
  energyConsumption: string;
};
export enum ReportStatus {
  QUEUED = 'QUEUED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  ERROR = 'ERROR',
}

export enum AiSummaryStatus {
  PENDING = 'PENDING',
  GENERATING = 'GENERATING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum AlarmType {
  INFO = 'info',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

type DataType = {
  averageDailyEfficiency?: number;
  dailyCycles?: number;
  dailyEnergyConsumption?: number;
  dailyRuntime?: number;
  dailyVolumetricFlow?: number;
};

type PumpData = {
  siteName: string;
  assetName: string;
  data: DataType;
};

export enum ReportType {
  PDF = 'PDF',
  ASSET = 'ASSET',
  DATA = 'DATA',
  PDF_SITE_STATUS_REPORT = 'PDF_SITE_STATUS',
}

type Report = {
  isFavorite: any;
  id: string;
  reportType: ReportType;
  templateId: string;
  name: string;
  customerId: string;
  actor: { id: string; type: string; firstName: string; lastName: string };
  createdAt: string;
  status: ReportStatus;
  details: string;
  retryNumber: string;
  s3Key: string;
  createdBy: { id: string; firstName: string; lastName: string };
  updatedAt: string;
  aiSummaryStatus: AiSummaryStatus | null;
  // scheduleType: string;
  // isFavorite: boolean;
};

type FilterItem = {
  key: string;
  label: string | JSX.Element;
};

type ColumnItem = {
  name: string;
  label: string;
  value: boolean;
};

interface FilterItemProps {
  label: string;
  name: string;
  onChange: (checked: boolean, name: string) => void;
  value: boolean;
}
type FilterGroup = {
  name: string;
  label: string;
};
type FilterItemType = {
  name: string;
  label: string;
  value: boolean;
};
type TimeFrameSelectValue = [string, string];
type AssetProperty = {
  id: string;
  asset: {
    type: string;
    property: Omit<Option, 'order'>;
  };
  aggregation: Omit<Option, 'order'>;
  columnStatistics: Record<string, boolean>;
  order?: number;
};
type TagData = {
  siteId: string;
  siteName: string;
  gatewayId: string;
  tagId: string;
  tagName: string;
  rawUnit: string;
  rawInputName: string;
};
type AssetPropertyOption = {
  value: string;
  label: string;
  available: boolean;
};
type TimeFrameToDateFunction = () => [string, string];
type TimeFrameValue = {
  value: string;
  label: string;
  parseToDate: TimeFrameToDateFunction;
};
type FormDataType = {
  [key: string]: string | boolean | any[] | FormDataType;
};
type DataColumn = {
  id: string;
  tag: TagData;
  aggregation: Omit<Option, 'order'> | null;
  columnStatistics: Record<string, boolean>;
  order?: number;
  unit?: string | null;
};

type ReportTemplateType = 'DATA' | 'ASSET' | 'PDF' | 'PDF_SITE_STATUS';

interface BaseReportTemplate {
  name: string;
  timezone: string;
  timeframe: {
    start: string;
    end: string;
  };
  dataAggregation: string;
}
interface BasePDFReportTemplate {
  name: string;
  timezone: string;
  timeframe: {
    start: string;
    end: string;
  };
  siteId: string;
  reportTemplate: string;
}

interface DataReportTemplate extends BaseReportTemplate {
  columns: {
    date: boolean;
    time: boolean;

    custom: {
      siteId: string;
      siteName: string;
      tagId: string;
      unit: string;
      tagName: string;
    }[];
  };
}

interface PDFReportTemplate extends BasePDFReportTemplate {
  mlssPerWeek: number;
  dataInterval: number;
  outlierTolerance: number;
  assetIds: string[];
}

interface ReportTemplateDesign {
  showStreametricLogo: boolean;
  coverImageKey?: string;
  siteImageKey?: string;
  // Presigned GETs from the single-template GET, kept for the preview; never sent back.
  coverImageUrl?: string;
  siteImageUrl?: string;
}

interface PDFReportSiteStatusTemplate extends BasePDFReportTemplate {
  tolerance: string;
  tagList: string[];
  design: ReportTemplateDesign;
}

interface AssetReportTemplate extends BaseReportTemplate {
  generateAiSummary?: boolean;
  columns: {
    default: {
      date: boolean;
      site_name: boolean;
      site_type: boolean;
      asset_name: boolean;
      asset_type: boolean;
    };

    custom: {
      site_id: string;
      site_name: string;
      tag_id: string;
      unit: string;
      tag_name: string;
    }[];
  };
}

type ApiResourceState = 'INITIAL' | 'LOADING' | 'SUCCESS' | 'ERROR';

type ColumnStatistics = Record<string, boolean>;

interface IAddColumnDialogProps {
  onSubmit: () => void;
  onCancel: () => void;
}

type AssetStateType = {
  assetProperties: AssetProperty[] | null;
  currentAssetProperty: AssetProperty | null;
  currentState: Record<string, any>;
};
type RangePumpData = PumpData & {
  date: string;
};

type AssetType = {
  assetName: string;
  data: DataType;
};

type Assets = {
  [key: string]: AssetType;
};

type SiteType = {
  siteName: string;
  assets: Assets;
};

type AssetReportAppliedFilters = {
  selectedItems: string[];
  assetTypes: string[];
  sitesTypes: string[];
};

export type {
  ColumnStatistics,
  ColumnItem,
  FormDataType,
  IAddColumnDialogProps,
  AssetPropertyOption,
  ReportTemplateType,
  AssetProperty,
  FilterItem,
  PumpModalData,
  PumpData,
  RangePumpData,
  SiteType,
  TagData,
  DataReportTemplate,
  TimeFrameValue,
  ApiResourceState,
  BaseReportTemplate,
  AssetReportTemplate,
  AssetType,
  DataColumn,
  AssetStateType,
  FilterGroup,
  Report,
  TimeFrameSelectValue,
  FilterItemType,
  FilterItemProps,
  AssetReportAppliedFilters,
  PDFReportTemplate,
  PDFReportSiteStatusTemplate,
  ReportTemplateDesign,
};
